/**
 * Shared Tamagui theme definitions used by both web and mobile apps.
 * Brand palette: Teal #0F9690 (accent), neutral-charcoal dark surfaces.
 *
 * Token scale (mirrors the BuyerFlo design system):
 *   background / backgroundStrong / backgroundRaised  → bg / bg-elev / bg-elev-2
 *   color / colorSoft / colorMuted / colorFaint        → fg / fg-soft / fg-muted / fg-faint
 *   borderColor / borderColorStrong                    → line / line-strong
 *   accentSoft / accentText                            → translucent accent tint / accent-on-surface text
 *   status* / status*Soft                              → semantic status solids + soft tints
 *   overlay                                            → modal/dialog scrim
 */

/** Brand teal — single source for the accent/primary colour (also used by non-Tamagui configs). */
export const BRAND_TEAL = '#0F9690'

export const darkTheme = {
    background: '#1A1B1D',
    background0: '#1A1B1D',
    background025: '#1E2022',
    background05: '#212325',
    background075: '#26282B',
    backgroundHover: '#26282B',
    backgroundPress: '#2A2C2F',
    backgroundFocus: '#2A2C2F',
    backgroundStrong: '#212325',
    backgroundRaised: '#2A2C2F',
    backgroundTransparent: 'rgba(26, 27, 29, 0)',
    color: '#E9EAEE',
    color0: '#E9EAEE',
    color025: '#EDEEF1',
    color05: '#F1F2F4',
    color075: '#F6F7F8',
    colorHover: '#F6F7F8',
    colorPress: '#F1F2F4',
    colorFocus: '#F6F7F8',
    colorTransparent: 'rgba(233, 234, 238, 0)',
    colorSoft: '#B1B6C2',
    colorMuted: '#7C8290',
    colorFaint: '#555A66',
    borderColor: 'rgba(255, 255, 255, 0.09)',
    borderColorStrong: 'rgba(255, 255, 255, 0.12)',
    borderColorHover: '#0F9690',
    borderColorFocus: '#0F9690',
    borderColorPress: '#0D8480',
    primary: BRAND_TEAL,
    primaryLight: '#1A4846',
    primaryMuted: '#165654',
    accentSoft: 'rgba(15, 150, 144, 0.14)',
    accentText: '#5FD0C6',
    statusOk: '#2DBA78',
    statusOkSoft: 'rgba(45, 186, 120, 0.14)',
    statusWarn: '#E9A23B',
    statusWarnSoft: 'rgba(233, 162, 59, 0.14)',
    statusDanger: '#E85959',
    statusDangerSoft: 'rgba(232, 89, 89, 0.14)',
    statusInfo: '#4F9BF0',
    statusInfoSoft: 'rgba(79, 155, 240, 0.14)',
    statusViolet: '#A47EF0',
    statusVioletSoft: 'rgba(164, 126, 240, 0.14)',
    statusNeutralSoft: 'rgba(255, 255, 255, 0.06)',
    overlay: 'rgba(0, 0, 0, 0.5)',
} as const

export const darkInputTheme = {
    background: '#212325',
    backgroundHover: '#26282B',
    backgroundPress: '#2A2C2F',
    backgroundFocus: '#212325',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    borderColorHover: '#0F9690',
    borderColorFocus: '#0F9690',
    borderColorPress: '#0D8480',
    // Tamagui's built-in `.is_Input/.is_TextArea::placeholder` rule reads a single
    // CSS var for placeholder colour. Defining it as a theme token here makes EVERY
    // input/textarea placeholder pale (= colorFaint) by default — so it reads as a
    // prompt, not prefilled text — with no per-input props and no global CSS. Per-input
    // `placeholderTextColor` still overrides it (set inline on the element).
    // The var name differs by Tamagui build: rc.38 reads `--placeholderColor`, while
    // 2.0.0 reads the internal `--t_placeholderColor`. We're currently on 2.0.0; define
    // both so the default survives a dependency re-resolution either way.
    placeholderColor: '#555A66',
    t_placeholderColor: '#555A66',
} as const

export const darkTextAreaTheme = {
    ...darkInputTheme,
} as const

export const lightTheme = {
    background: '#F7F7F5',
    background0: '#F7F7F5',
    backgroundHover: '#F0F0EE',
    backgroundPress: '#E9E9E6',
    backgroundFocus: '#F0F0EE',
    backgroundStrong: '#FFFFFF',
    backgroundRaised: '#FFFFFF',
    backgroundTransparent: 'rgba(247, 247, 245, 0)',
    color: '#14171C',
    colorHover: '#3A4150',
    colorPress: '#6B7282',
    colorFocus: '#3A4150',
    colorTransparent: 'rgba(20, 23, 28, 0)',
    colorSoft: '#4A5161',
    colorMuted: '#6B7282',
    colorFaint: '#9AA1AD',
    borderColor: '#E6E6E2',
    borderColorStrong: '#D4D4CF',
    borderColorHover: '#0F9690',
    borderColorFocus: '#0F9690',
    borderColorPress: '#0D8480',
    primary: BRAND_TEAL,
    primaryLight: '#E0F5F4',
    primaryMuted: '#B8E5E3',
    accentSoft: 'rgba(15, 150, 144, 0.12)',
    accentText: '#0A726C',
    statusOk: '#2DBA78',
    statusOkSoft: 'rgba(45, 186, 120, 0.16)',
    statusWarn: '#E9A23B',
    statusWarnSoft: 'rgba(233, 162, 59, 0.16)',
    statusDanger: '#E85959',
    statusDangerSoft: 'rgba(232, 89, 89, 0.14)',
    statusInfo: '#4F9BF0',
    statusInfoSoft: 'rgba(79, 155, 240, 0.14)',
    statusViolet: '#A47EF0',
    statusVioletSoft: 'rgba(164, 126, 240, 0.14)',
    statusNeutralSoft: 'rgba(0, 0, 0, 0.05)',
    overlay: 'rgba(0, 0, 0, 0.5)',
} as const

export const lightInputTheme = {
    background: '#FFFFFF',
    backgroundHover: '#F7F7F5',
    backgroundPress: '#F0F0EE',
    backgroundFocus: '#FFFFFF',
    borderColor: '#E6E6E2',
    borderColorHover: '#0F9690',
    borderColorFocus: '#0F9690',
    borderColorPress: '#0D8480',
    // See darkInputTheme — pale placeholder default (= light colorFaint).
    placeholderColor: '#9AA1AD',
    t_placeholderColor: '#9AA1AD',
} as const

export const lightTextAreaTheme = {
    ...lightInputTheme,
} as const

export const lightPrimaryButtonTheme = {
    background: '#0F9690',
    backgroundHover: '#0D8480',
    backgroundPress: '#0B726D',
    backgroundFocus: '#0D8480',
    color: '#FFFFFF',
    colorHover: '#FFFFFF',
    colorPress: '#FFFFFF',
    colorFocus: '#FFFFFF',
    borderColor: '#0D8480',
    borderColorHover: '#0B726D',
    borderColorPress: '#0B726D',
    borderColorFocus: '#0D8480',
} as const

export const darkPrimaryButtonTheme = {
    background: '#0F9690',
    backgroundHover: '#12ADA6',
    backgroundPress: '#0D8480',
    backgroundFocus: '#12ADA6',
    color: '#FFFFFF',
    colorHover: '#FFFFFF',
    colorPress: '#FFFFFF',
    colorFocus: '#FFFFFF',
    borderColor: '#0F9690',
    borderColorHover: '#12ADA6',
    borderColorPress: '#0D8480',
    borderColorFocus: '#12ADA6',
} as const

/**
 * Shifts Tamagui's built-in green-hued HSLA colors to teal (~177) to match
 * the brand palette. Use this when aliasing green sub-themes as "primary".
 */
function greenToTeal(theme: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(theme)) {
        if (typeof value === 'string') {
            result[key] = value.replace(
                /hsla\(([\d.]+),/g,
                (_match, hStr) => {
                    const h = parseFloat(hStr)
                    // Tamagui green palette uses hues 130-160; shift to teal 177
                    if (h >= 130 && h <= 160) {
                        return `hsla(177,`
                    }
                    return _match
                },
            )
        } else {
            result[key] = value
        }
    }
    return result
}

/**
 * Takes the default Tamagui themes and returns teal-shifted "primary" aliases
 * for every green sub-theme (e.g. light_green_Button → light_primary_Button).
 */
export function buildPrimaryThemes(
    themes: Record<string, any>,
): Record<string, any> {
    return Object.fromEntries(
        Object.entries(themes)
            .filter(([key]) => key.includes('green'))
            .map(([key, value]) => [
                key.replace('green', 'primary'),
                greenToTeal(value),
            ]),
    )
}
